import {
  endpoints,
  GuestEmployer,
  ImportInvitesResult,
  SmartInvite,
  SmartInviteAssignSpareBody,
  SmartInviteBlackout,
  SmartInviteBlackoutList,
  SmartInviteCatalogueView,
  SmartInviteAcceptMenuResult,
  SmartInviteCreatedBlackout,
  SmartInviteDeclaration,
  SmartInviteDeclineMenuResult,
  SmartInviteEmployerList,
  SmartInviteEmployerMergeScope,
  SmartInviteExperienceAvailability,
  SmartInviteTicketsReceived,
  SmartInviteTicketFiles,
  SmartInviteTicketFilesUploadBody,
  SmartInviteFulfilment,
  SmartInviteFulfilmentExperience,
  SmartInviteMyTickets,
  SmartInviteTicketAssignmentBody,
  SmartInviteGuestMenu,
  SmartInviteJoinShareLinkResult,
  SmartInviteLimitations,
  SmartInviteOfferAvailability,
  SmartInviteReserveQueue,
  SmartInviteRoster,
  SmartInviteRosterEmployerList,
  SmartInviteHostParticipation,
  SetSmartInviteHostParticipationBody,
  SmartInviteSelectOfferBody,
  SmartInviteSelectionResult,
  SmartInviteVipPushBody,
  SmartInviteShareLink,
  SmartInviteSpareAssignmentResult,
  SmartInviteStatus,
  SmartInvitesConfig,
  SmartInviteApproval,
  SmartInviteApprovalDecisionBody,
  SmartInviteApprovalState,
  SmartInviteBlackoutRemediation,
  SmartInviteBlackoutRemediationDecisionBody,
  SmartInviteBlackoutRemediationState,
} from '@seaters/constants';
import type { AxiosError } from 'axios';
import { api } from '../axios-instance';
import { readBlobError } from '../utils/readBlobError';

// The Node-owned Smart Invites surface: the per-fan-group capability flag, the Host's experience
// catalogue, invitation and menu CRUD, the employer directory and the fan group's limitations.
const base = endpoints.smartInvites;

// --- Machine-readable backend error codes ---
//
// Lets a consumer branch on `smartInviteErrorCode(error) === 'EmailNotVerified'` instead of
// string-matching an error message. Every coded error the Node surface raises today, checked
// against its throw sites. Listing one the backend never throws is a caller branching on the
// impossible; listing too few drops that failure into the caller's generic handler, which is how
// a Guest got "please try again" for a conflict only they could resolve.
//
// Java's Smart Invites routes do NOT belong here: they throw i18n keys directly
// (`si_selection_over_cap`, …), which the fan view renders through `t()`.
export const SMART_INVITE_ERROR_CODES = [
  // Not SCREAMING_CASE on purpose: VerifiedEmailGuard throws
  // `ForbiddenException('EmailNotVerified', …)`, so that exact string is what reaches the wire.
  'EmailNotVerified',
  'ALREADY_JOINED',
  'BLACKOUT_ALREADY_ACTIVE',
  'BLACKOUT_ALREADY_ENDED',
  'CANNOT_MERGE_SELF',
  'EMPLOYER_NAME_REQUIRED',
  'EMPLOYER_NOT_ACTIVE',
  'EXPERIENCE_NOT_IN_CATALOGUE',
  // `rethrowOfferUniqueViolationAsConflict` turns the offer-uniqueness 23505 into this 409 — the
  // one invitation per (Guest, Experience) rule, hit when another Host already offers it.
  'GUEST_ALREADY_INVITED_TO_EXPERIENCE',
  'INVITE_AWAITING_CONFIRMATION',
  'INVITE_DECLINED',
  'INVITE_HAS_ACTIVE_SELECTION',
  'INVITE_HAS_NO_OFFERS',
  'INVITE_NOT_PENDING_REVIEW',
  'INVITE_NOT_REMOVABLE',
  // EmployerDirectoryService.mergeWithinFanGroup: the employer to merge has an active blackout here.
  'MERGE_SOURCE_BLACKED_OUT',
  'MERGE_SOURCE_NOT_ACTIVE',
  'MERGE_TARGET_NOT_ACTIVE',
  'OFFER_ALREADY_CONFIRMED',
  // SmartInvitesFanGroupCompanyService.requireCompanyId: the fan group belongs to no Company.
  'SMART_INVITES_REQUIRES_COMPANY',
  // SmartInviteExperienceFreezeService: the FGO marked this Experience's tickets received.
  'SI_EXPERIENCE_FROZEN',
  // SmartInviteMenuService: only voucher Experiences can be offered.
  'SI_EXPERIENCE_REQUIRES_VOUCHER',
] as const;
export type SmartInviteErrorCode = (typeof SMART_INVITE_ERROR_CODES)[number];

// Nest's `ConflictException(code, description)` serializes to `{ message: code, error:
// description }` (HttpException.createBody), so the machine-readable code arrives in
// `.response.data.message` — there is no `errorCode` field on the wire.
export const smartInviteErrorCode = (
  error: unknown
): SmartInviteErrorCode | undefined => {
  const code = (error as AxiosError<{ message?: string }>)?.response?.data
    ?.message;
  return SMART_INVITE_ERROR_CODES.includes(code as SmartInviteErrorCode)
    ? (code as SmartInviteErrorCode)
    : undefined;
};

// --- Capability ---
// backend/node/src/api/smartInvites/domains/capability/capability.controller.ts

export const fetchSmartInvitesConfig = (
  fanGroupId: string
): Promise<SmartInvitesConfig> =>
  api.get(`${base}/config/${endpoints.fanGroups}/${fanGroupId}`);

// Slug-keyed twin of the read above, for the fan area: it has no fan group id at guard time, and a
// Guest is not a member of the audience. Unknown slug answers `{ enabled: false }`.
export const fetchSmartInvitesAudienceCapability = (
  slug: string
): Promise<SmartInvitesConfig> => api.get(`${base}/config/audiences/${slug}`);

export const updateSmartInvitesConfig = (
  fanGroupId: string,
  enabled: boolean
): Promise<SmartInvitesConfig> =>
  api.put(`${base}/config/${endpoints.fanGroups}/${fanGroupId}`, { enabled });

// --- Host curation ---

export interface ListSmartInviteCatalogueParams {
  page?: number;
  size?: number;
  search?: string;
  categoryId?: string;
  venueId?: string;
  /** Inclusive ISO lower/upper bounds on the event start date. */
  from?: string;
  to?: string;
}

export const fetchSmartInviteCatalogue = (
  fanGroupId: string,
  params: ListSmartInviteCatalogueParams
): Promise<SmartInviteCatalogueView> =>
  api.get(
    `${base}/${endpoints.host}/${endpoints.fanGroups}/${fanGroupId}/${endpoints.experiences}`,
    { params }
  );

export interface ListSmartInvitesParams {
  page: number;
  size: number;
  status?: SmartInviteStatus;
  search?: string;
}

export const fetchSmartInviteRoster = (
  fanGroupId: string,
  params: ListSmartInvitesParams
): Promise<SmartInviteRoster> =>
  api.get(
    `${base}/${endpoints.host}/${endpoints.fanGroups}/${fanGroupId}/invites`,
    { params }
  );

export const fetchSmartInvite = (
  fanGroupId: string,
  inviteId: string
): Promise<SmartInvite> =>
  api.get(
    `${base}/${endpoints.host}/${endpoints.fanGroups}/${fanGroupId}/invites/${inviteId}`
  );

export interface CreateSmartInviteBody {
  email: string;
  firstName?: string;
  lastName?: string;
  /** Required: the employer as the Host typed it, found or added in the Company's directory. */
  employerName: string;
  locale?: string;
  publicOfficial?: boolean;
  offers?: Array<{ waitingListId: string; plusOneAllowed: boolean }>;
}

export const createSmartInvite = (
  fanGroupId: string,
  body: CreateSmartInviteBody
): Promise<SmartInvite> =>
  api.post(
    `${base}/${endpoints.host}/${endpoints.fanGroups}/${fanGroupId}/invites`,
    body
  );

// `employerName` is optional on the wire ON PURPOSE: a row without one must fail that row alone,
// not the whole batch, so the backend reports it through `failedRows`, each carrying a reason,
// rather than 400ing the request.
export interface ImportSmartInviteRow {
  email: string;
  firstName?: string;
  lastName?: string;
  locale?: string;
  publicOfficial?: boolean;
  employerName?: string;
}

export interface ImportSmartInvitesBody {
  rows: ImportSmartInviteRow[];
  offers?: Array<{ waitingListId: string; plusOneAllowed: boolean }>;
}

export const importSmartInvites = (
  fanGroupId: string,
  body: ImportSmartInvitesBody
): Promise<ImportInvitesResult> =>
  api.post(
    `${base}/${endpoints.host}/${endpoints.fanGroups}/${fanGroupId}/invites/import`,
    body
  );

export const deleteSmartInvite = (
  fanGroupId: string,
  inviteId: string
): Promise<void> =>
  api.delete(
    `${base}/${endpoints.host}/${endpoints.fanGroups}/${fanGroupId}/invites/${inviteId}`
  );

export interface SetSmartInviteMenuOffer {
  waitingListId: string;
  plusOneAllowed: boolean;
}

// Adds or updates the supplied experiences on the invite's menu. The backend applies `offers` as
// an additive merge, not a wholesale replacement: existing offers omitted from the array survive,
// and removals are stated explicitly through `removeWaitingListIds`.
export interface SetSmartInviteMenuBody {
  offers: SetSmartInviteMenuOffer[];
  removeWaitingListIds?: string[];
  /** Required to edit a declined invite (reopens it as an unsent draft); only "Invite again" sets it. */
  reopenDeclined?: boolean;
}

export const setSmartInviteMenu = (
  fanGroupId: string,
  inviteId: string,
  body: SetSmartInviteMenuBody
): Promise<SmartInvite> =>
  api.put(
    `${base}/${endpoints.host}/${endpoints.fanGroups}/${fanGroupId}/invites/${inviteId}/menu`,
    body
  );

export type SmartInviteHostSendKind =
  | 'invite'
  | 'reminder'
  | 'additionalExperiences';

export interface SmartInviteMailCopyBody {
  subject: string;
  messageHtml: string;
  signOff?: string;
}

export interface SendSmartInviteBody {
  kind?: SmartInviteHostSendKind;
  copy?: SmartInviteMailCopyBody;
}

export const sendSmartInvite = (
  fanGroupId: string,
  inviteId: string,
  body: SendSmartInviteBody = {}
): Promise<SmartInvite> =>
  api.post(
    `${base}/${endpoints.host}/${endpoints.fanGroups}/${fanGroupId}/invites/${inviteId}/${endpoints.send}`,
    body
  );

// Confirms a self-registered applicant (status pendingReview, arrived via a share link) into the
// normal invited flow.
export interface ConfirmSmartInviteApplicantBody {
  /** Required: the Host records (or corrects) the applicant's employer at confirm time. */
  employerName: string;
  publicOfficial?: boolean;
}

export const confirmSmartInviteApplicant = (
  fanGroupId: string,
  inviteId: string,
  body: ConfirmSmartInviteApplicantBody
): Promise<SmartInvite> =>
  api.post(
    `${base}/${endpoints.host}/${endpoints.fanGroups}/${fanGroupId}/invites/${inviteId}/confirm-applicant`,
    body
  );

// Mints a share link a host can hand to prospective guests. Its token is returned directly and
// consumed once by the caller — there is no cached list of share links.
export const createSmartInviteShareLink = (
  fanGroupId: string
): Promise<SmartInviteShareLink> =>
  api.post(
    `${base}/${endpoints.host}/${endpoints.fanGroups}/${fanGroupId}/share-links`
  );

// --- Guest ingress ---

export interface JoinSmartInviteShareLinkBody {
  firstName?: string;
  lastName?: string;
  locale?: string;
}

// Redeems a Host's shared invite link: lands the caller's own (VerifiedEmailGuard-resolved)
// account in `pendingReview`, with no menu until the Host confirms.
export const joinSmartInviteShareLink = (
  token: string,
  body: JoinSmartInviteShareLinkBody
): Promise<SmartInviteJoinShareLinkResult> =>
  api.post(`${base}/${endpoints.guest}/share-links/${token}/join`, body);

export interface DeclineSmartInviteShareLinkBody {
  note?: string;
}

// Turns the Host's link down from its join card, before joining: the same decline as the menu's.
export const declineSmartInviteShareLink = (
  token: string,
  body: DeclineSmartInviteShareLinkBody = {}
): Promise<SmartInviteDeclineMenuResult> =>
  api.post(`${base}/${endpoints.guest}/share-links/${token}/decline`, body);

// --- Guest menu ---
//
// Every call is identity-bound: no fan group in the path, because one Guest identity can hold
// invitations from several. The server resolves the caller's guest rows (and drops the ones whose
// fan group has the capability off) before any handler runs.

// The 404 (not a Guest, or Smart Invites off) is the answer "no invites". As an error, React
// Query would keep the last menu on a refetch, and a removed invitation would stay on screen.
export const fetchMySmartInviteMenu =
  async (): Promise<SmartInviteGuestMenu> => {
    try {
      return await api.get(`${base}/${endpoints.guest}/menu`);
    } catch (error) {
      if ((error as AxiosError)?.response?.status === 404) {
        return { invites: [] };
      }
      throw error;
    }
  };

// `invited → registered`. Until then the invitation does not apply and Java refuses a claim on it.
export const acceptSmartInviteMenu = (
  inviteId: string
): Promise<SmartInviteAcceptMenuResult> =>
  api.post(`${base}/${endpoints.guest}/menu/invites/${inviteId}/accept`);

export interface DeclineSmartInviteMenuBody {
  note?: string;
}

// Declining addresses the WHOLE INVITE (`menu/invites/:inviteId/decline`), never a single offer:
// the server has no notion of declining one experience.
export const declineSmartInviteMenu = (
  inviteId: string,
  body: DeclineSmartInviteMenuBody = {}
): Promise<SmartInviteDeclineMenuResult> =>
  api.post(`${base}/${endpoints.guest}/menu/invites/${inviteId}/decline`, body);

// --- Guest Compliance Declaration ---
//
// Per fan group: the Declaration belongs to the caller's guest row there. 404 unless the fan
// group's FGO asks for one.

export const fetchMySmartInviteDeclaration = (
  fanGroupId: string
): Promise<SmartInviteDeclaration> =>
  api.get(
    `${base}/${endpoints.guest}/${endpoints.fanGroups}/${fanGroupId}/declaration`
  );

// Partial update: omitted means "unchanged", a `null` cap clears it. Send only what changed —
// never a spread of the fetched SmartInviteDeclaration (.claude/rules/data-integrity.md).
export interface UpdateMySmartInviteDeclarationBody {
  valueCapPerExperienceCents?: number | null;
  valueCapPerYearCents?: number | null;
  maxEventsPerPersonPerYear?: number | null;
  forbidsAllHospitality?: boolean;
}

export const updateMySmartInviteDeclaration = (
  fanGroupId: string,
  body: UpdateMySmartInviteDeclarationBody
): Promise<SmartInviteDeclaration> =>
  api.patch(
    `${base}/${endpoints.guest}/${endpoints.fanGroups}/${fanGroupId}/declaration`,
    body
  );

// --- Guest selection (Java, not Node) ---
//
// `v2/smart-invites/…` on purpose: `invitations/…` is allow-listed to Node at the edge, so a Java
// command must live outside it. smartInvites.routing.spec.ts pins both halves of that split.
const selectionsBase = endpoints.smartInviteSelections;

// A refused download answers JSON, which a blob request delivers as a Blob: read it first so the
// caller's `response.data.message` is there.
const getBlob = async (
  url: string,
  params?: Record<string, string>
): Promise<Blob> => {
  try {
    return await api.get(url, { responseType: 'blob', params });
  } catch (error) {
    throw await readBlobError(error);
  }
};

export const selectSmartInviteOffer = (
  offerId: string,
  body: SmartInviteSelectOfferBody = {}
): Promise<SmartInviteSelectionResult> =>
  api.post(`${selectionsBase}/offers/${offerId}/selection`, body);

export const releaseSmartInviteSelection = (
  selectionId: string
): Promise<SmartInviteSelectionResult> =>
  api.delete(`${selectionsBase}/selections/${selectionId}`);

// Advisory and lock-free. Authorised by the offer chain alone, so a fan who was not offered this
// Experience gets the same 404 as one asking about an offer that does not exist.
export const fetchSmartInviteOfferAvailability = (
  offerId: string
): Promise<SmartInviteOfferAvailability> =>
  api.get(`${selectionsBase}/offers/${offerId}/availability`);

// --- Host party and VIP push (Java, not Node) ---
//
// Same `v2/smart-invites` prefix and the same reason: these two consume capacity through the Java
// selection command.

export const fetchSmartInviteHostParticipation = (
  waitingListId: string
): Promise<SmartInviteHostParticipation> =>
  api.get(`${selectionsBase}/experiences/${waitingListId}/participation`);

// Full replacement of the Host's declared party, guarded by the `version` from the read above.
// Never build this body by spreading a stale render's copy — that is the erasure
// .claude/rules/data-integrity.md forbids, and the version is what makes it a 409 instead.
export const setSmartInviteHostParticipation = (
  waitingListId: string,
  body: SetSmartInviteHostParticipationBody
): Promise<SmartInviteHostParticipation> =>
  api.put(`${selectionsBase}/experiences/${waitingListId}/participation`, body);

// Addressed by OFFER, not by a selection: the Guest a Host most wants to push is the one who has
// not answered their menu and so has no selection to name.
export const vipPushSmartInviteOffer = (
  offerId: string,
  body: SmartInviteVipPushBody = {}
): Promise<SmartInviteSelectionResult> =>
  api.post(`${selectionsBase}/offers/${offerId}/vip`, body);

// --- FGO allocation surface (Java) ---
//
// Experience-addressed, and answering 403 rather than the Guest routes' 404: the caller is already
// inside the back office, so there is nothing to hide from them.

export const fetchSmartInviteExperienceAvailability = (
  waitingListId: string
): Promise<SmartInviteExperienceAvailability> =>
  api.get(`${selectionsBase}/experiences/${waitingListId}/availability`);

export const fetchSmartInviteReserveQueue = (
  waitingListId: string
): Promise<SmartInviteReserveQueue> =>
  api.get(`${selectionsBase}/experiences/${waitingListId}/reserve`);

export const assignSmartInviteSpare = (
  waitingListId: string,
  body: SmartInviteAssignSpareBody
): Promise<SmartInviteSpareAssignmentResult> =>
  api.post(`${selectionsBase}/experiences/${waitingListId}/spare`, body);

// Idempotent both ways. Undo is refused once a ticket of the Experience is on its way.
export const markSmartInviteTicketsReceived = (
  waitingListId: string
): Promise<SmartInviteTicketsReceived> =>
  api.post(`${selectionsBase}/experiences/${waitingListId}/tickets-received`);

export const undoSmartInviteTicketsReceived = (
  waitingListId: string
): Promise<SmartInviteTicketsReceived> =>
  api.delete(`${selectionsBase}/experiences/${waitingListId}/tickets-received`);

// Allowed only after tickets received. The PDFs are put to S3 first, through createPrivateUploadURL.
// A large upload can outlive the proxy timeout and still be recorded. A lost or 5xx reply checks
// the list for its batch before failing, so the FGO is not told to upload it again.
export const uploadSmartInviteTicketFiles = async (
  waitingListId: string,
  body: SmartInviteTicketFilesUploadBody
): Promise<SmartInviteTicketFiles> => {
  try {
    return await api.post(
      `${selectionsBase}/experiences/${waitingListId}/ticket-files`,
      body
    );
  } catch (error) {
    const status = (error as AxiosError)?.response?.status;
    if (status !== undefined && status < 500) throw error;
    const view = await fetchSmartInviteTicketFiles(waitingListId);
    if (view.files.some((file) => file.batchId === body.batchId)) return view;
    throw error;
  }
};

export const fetchSmartInviteTicketFiles = (
  waitingListId: string
): Promise<SmartInviteTicketFiles> =>
  api.get(`${selectionsBase}/experiences/${waitingListId}/ticket-files`);

// Refused unless the file is still available.
export const deleteSmartInviteTicketFile = (
  waitingListId: string,
  ticketFileId: string
): Promise<SmartInviteTicketFiles> =>
  api.delete(
    `${selectionsBase}/experiences/${waitingListId}/ticket-files/${ticketFileId}`
  );

export const downloadSmartInviteTicketFile = (
  waitingListId: string,
  ticketFileId: string
): Promise<Blob> =>
  getBlob(
    `${selectionsBase}/experiences/${waitingListId}/ticket-files/${ticketFileId}/download`
  );

// --- Fulfilment: binding ticket files to people and sending them ---

const fulfilmentBase = (waitingListId: string) =>
  `${selectionsBase}/experiences/${waitingListId}/fulfilment`;

// Where the calling Host has people to ticket; an FGO gets every Host's.
export const fetchSmartInviteFulfilmentExperiences = (
  fanGroupId: string
): Promise<SmartInviteFulfilmentExperience[]> =>
  api.get(`${selectionsBase}/fan-groups/${fanGroupId}/fulfilment/experiences`);

export const fetchSmartInviteFulfilment = (
  waitingListId: string
): Promise<SmartInviteFulfilment> => api.get(fulfilmentBase(waitingListId));

// Moves a file already on someone, and gives back the file a taken slot held.
export const assignSmartInviteTicketFile = (
  waitingListId: string,
  body: SmartInviteTicketAssignmentBody
): Promise<SmartInviteFulfilment> =>
  api.put(`${fulfilmentBase(waitingListId)}/assignments`, body);

export const unassignSmartInviteTicketFile = (
  waitingListId: string,
  ticketFileId: string
): Promise<SmartInviteFulfilment> =>
  api.delete(`${fulfilmentBase(waitingListId)}/assignments/${ticketFileId}`);

// Refused unless every slot of the selection holds an assigned file.
export const sendSmartInviteTickets = (
  waitingListId: string,
  selectionId: string
): Promise<SmartInviteFulfilment> =>
  api.post(`${fulfilmentBase(waitingListId)}/selections/${selectionId}/send`);

export const resendSmartInviteTickets = (
  waitingListId: string,
  selectionId: string
): Promise<SmartInviteFulfilment> =>
  api.post(`${fulfilmentBase(waitingListId)}/selections/${selectionId}/resend`);

// hostUserId only when an FGO acts for a Host.
export const sendSmartInviteHostPartyTickets = (
  waitingListId: string,
  hostUserId?: string
): Promise<SmartInviteFulfilment> =>
  api.post(`${fulfilmentBase(waitingListId)}/host-party/send`, undefined, {
    params: hostUserId ? { hostUserId } : undefined,
  });

export const downloadSmartInviteHostPartyTickets = (
  waitingListId: string,
  hostUserId?: string
): Promise<Blob> =>
  getBlob(
    `${fulfilmentBase(waitingListId)}/host-party/download`,
    hostUserId ? { hostUserId } : undefined
  );

export const fetchMySmartInviteTickets = (
  waitingListId: string
): Promise<SmartInviteMyTickets> =>
  api.get(`${selectionsBase}/my-tickets/${waitingListId}`);

export const downloadMySmartInviteTickets = (
  waitingListId: string
): Promise<Blob> =>
  getBlob(`${selectionsBase}/my-tickets/${waitingListId}/download`);

// --- Compliance ---

export const fetchSmartInviteLimitations = (
  fanGroupId: string
): Promise<SmartInviteLimitations> =>
  api.get(
    `${base}/compliance/${endpoints.fanGroups}/${fanGroupId}/limitations`
  );

// Partial update: every field is optional and omitted means "unchanged" on the backend; a `null`
// on a cap field is the explicit "clear this cap" signal (data-integrity.md rule 5), distinct from
// omitting it. Never build this body by spreading a fetched SmartInviteLimitations — that is the
// stale-read erasure .claude/rules/data-integrity.md forbids.
export interface UpdateSmartInviteLimitationsBody {
  valueCapPerExperienceCents?: number | null;
  valueCapPerYearCents?: number | null;
  maxEventsPerPersonPerYear?: number | null;
  maxDistinctGuestsPerCompanyPerYear?: number | null;
  maxTicketsPerInvite?: number | null;
  approachingThresholdPct?: number;
  hostsCanApprove?: boolean;
  hostClearanceMarginPct?: number;
  askGuestsForDeclaration?: boolean;
  evaluationSurveyInstanceId?: string | null;
}

export const updateSmartInviteLimitations = (
  fanGroupId: string,
  body: UpdateSmartInviteLimitationsBody
): Promise<SmartInviteLimitations> =>
  api.put(
    `${base}/compliance/${endpoints.fanGroups}/${fanGroupId}/limitations`,
    body
  );

export interface ListSmartInviteEmployersParams {
  search?: string;
  page?: number;
  /** The server caps this at 200 and defaults to 20 — a picker that ignores it shows 20 silently. */
  size?: number;
}

// FGO/Host directory search. `guest_employer` has no fangroup_id column; the fan-groups/:id
// nesting exists only so the handler has a fan group to run the availability gate against.
// The route paginates (PaginatedEmployersView), so the caller gets `total` and can page rather
// than believing the first page is the whole directory.
export const fetchSmartInviteEmployers = (
  fanGroupId: string,
  params: ListSmartInviteEmployersParams = {}
): Promise<SmartInviteEmployerList> =>
  api.get(
    `${base}/compliance/${endpoints.fanGroups}/${fanGroupId}/${endpoints.employers}`,
    { params }
  );

// `:fromId` is merged INTO `intoId`. FGO: this fan group only; a Company Admin may merge
// company-wide.
export const mergeSmartInviteEmployers = (
  fanGroupId: string,
  fromId: string,
  intoId: string,
  scope: SmartInviteEmployerMergeScope
): Promise<GuestEmployer> =>
  api.post(
    `${base}/compliance/${endpoints.fanGroups}/${fanGroupId}/${endpoints.employers}/${fromId}/merge`,
    { intoId, scope }
  );

// Unlike the directory search above, scoped to the employers this fan group's guests have on file.
export const fetchSmartInviteRosterEmployers = (
  fanGroupId: string,
  params: ListSmartInviteEmployersParams = {}
): Promise<SmartInviteRosterEmployerList> =>
  api.get(
    `${base}/compliance/${endpoints.fanGroups}/${fanGroupId}/${endpoints.rosterEmployers}`,
    { params }
  );

export const fetchSmartInviteBlackouts = (
  fanGroupId: string
): Promise<SmartInviteBlackoutList> =>
  api.get(
    `${base}/compliance/${endpoints.fanGroups}/${fanGroupId}/${endpoints.blackouts}`
  );

export interface CreateSmartInviteBlackoutBody {
  employerId: string;
  reason?: string;
}

export const createSmartInviteBlackout = (
  fanGroupId: string,
  body: CreateSmartInviteBlackoutBody
): Promise<SmartInviteCreatedBlackout> =>
  api.post(
    `${base}/compliance/${endpoints.fanGroups}/${fanGroupId}/${endpoints.blackouts}`,
    body
  );

// Ends, never deletes: Java's remediation records reference the blackout id.
export const endSmartInviteBlackout = (
  fanGroupId: string,
  blackoutId: string
): Promise<SmartInviteBlackout> =>
  api.post(
    `${base}/compliance/${endpoints.fanGroups}/${fanGroupId}/${endpoints.blackouts}/${blackoutId}/end`
  );

// --- Approvals queue (Java) ---
//
// Both lists answer a plain page, newest first, with no total. A Host's approvals list is scoped
// by Java to the claims they host; remediation records are FGO-only (403 for a Host).

export interface ListSmartInviteApprovalsParams {
  state?: SmartInviteApprovalState;
  page?: number;
  size?: number;
}

export const fetchSmartInviteApprovals = (
  fanGroupId: string,
  params: ListSmartInviteApprovalsParams = {}
): Promise<SmartInviteApproval[]> =>
  api.get(`${selectionsBase}/fan-groups/${fanGroupId}/approvals`, { params });

export const decideSmartInviteApproval = (
  approvalId: string,
  body: SmartInviteApprovalDecisionBody
): Promise<SmartInviteApproval> =>
  api.post(`${selectionsBase}/approvals/${approvalId}/decide`, body);

export interface ListSmartInviteBlackoutRemediationsParams {
  // Omitted: Java lists the undecided ones (open and unavailable).
  state?: SmartInviteBlackoutRemediationState[];
  page?: number;
  size?: number;
}

export const fetchSmartInviteBlackoutRemediations = (
  fanGroupId: string,
  params: ListSmartInviteBlackoutRemediationsParams = {}
): Promise<SmartInviteBlackoutRemediation[]> =>
  api.get(`${selectionsBase}/fan-groups/${fanGroupId}/blackout-remediations`, {
    params,
    // Spring binds a repeated `state=`, not axios's default `state[]=`.
    paramsSerializer: { indexes: null },
  });

export const decideSmartInviteBlackoutRemediation = (
  remediationId: string,
  body: SmartInviteBlackoutRemediationDecisionBody
): Promise<SmartInviteBlackoutRemediation> =>
  api.post(
    `${selectionsBase}/blackout-remediations/${remediationId}/decide`,
    body
  );
